'use client';

import type { AnalyticsEventInput, AnalyticsEventType } from '@vuganawe/types';
import { API_URL } from './api/client';
import { analyticsAllowed } from './consent';

/**
 * First-party, privacy-light analytics: random session id, no cookies, no PII.
 * Runs ONLY after the visitor accepts analytics in the cookie banner.
 * Fire-and-forget — analytics must never break the page.
 */

const SESSION_KEY = 'vn_session_id';
const ANALYTICS_URL = `${API_URL}/events`;

function getSessionId(): string {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-session';
  }
}

export function track(
  eventType: AnalyticsEventType,
  page: string,
  label?: string,
  metadata?: Record<string, string | number | boolean>,
): void {
  if (typeof window === 'undefined') return;
  if (!analyticsAllowed()) return;
  const payload: AnalyticsEventInput = {
    eventType,
    page,
    label,
    sessionId: getSessionId(),
    metadata,
  };
  try {
    const body = JSON.stringify(payload);
    // sendBeacon survives page navigation; fall back to fetch with keepalive.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ANALYTICS_URL, blob);
    } else {
      void fetch(ANALYTICS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    }
  } catch {
    // never let analytics break UX
  }
}
